import React from 'react';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';
import { useCallback, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { Alert, ToggleButton } from 'react-bootstrap';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import { FormLabel } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import getAjv from '../../helpers/get-ajv';
import * as addFormats from 'ajv-formats';
import parseJSON from '../../helpers/parse-json';
import prettify from '../../helpers/prettify';
import jsonSchemaTemplate from '../../templates/json-schema.json';

const textArea = { width: '700px', margin: '20px', fontFamily: 'Courier New' }
const link = { width: '200px', height: '50px', margin: '10px', padding: '5px', color: 'red', backgroundColor: 'lightgray', textAlign: 'center' }

function IsJSONSchemaValid() {
  const [alertMsg, setAlertMsg] = useState('');
  const [jsonSchema, setJsonSchema] = useState('');
  const [results, setResults] = useState([]);
  const [isStrict, setIsStrict] = useState(true);

  const showAlertMsg = useCallback((msg) => {
    setAlertMsg(msg);
  }, [setAlertMsg]);

  const checkParsedJSON = useCallback((parsed) => {
    if (!parsed) {
      showAlertMsg('JSON is not parsed!');
      return false;
    }
    else if (Object.keys(parsed).length === 0) {
      showAlertMsg('JSON is empty!');
      return false;
    }

    return true;
  }, [showAlertMsg]);

  const handleJsonSchemaChange = useCallback((e) => {
    if (!e.target.value) {
      return;
    }
    setResults([]);

    const parsed = parseJSON(e.target.value);

    if (checkParsedJSON(parsed)) {
      setJsonSchema(parsed);
      prettify('json');
    }
    else {
      setJsonSchema(e.target.value);
    }

  }, [showAlertMsg]);

  const handleIsJSONSchemaValid = useCallback((mode) => {

    setAlertMsg('');

    let ajv = getAjv(mode, isStrict);

    addFormats(ajv)
    setResults([]);

    let jsonSchemaList = [];
    if (Object.prototype.toString.call(jsonSchema) !== '[object Array]') {
      jsonSchemaList.push(jsonSchema);
    }
    else {
      jsonSchemaList = jsonSchema;
    }

    for (let one of jsonSchemaList) {

      // if description is on wrong place
      if (one.description) {
        showAlertMsg('Description is not allowed in JSON Schema on root level! Please include it in the function.');
        return;
      }

      if (!one.function.parameters) {
        showAlertMsg('The hierarchy of JSON Schema is not correct! Please include as schema[idx].function.parameters.');
        return;
      }
      if (!one.function.parameters.properties) {
        showAlertMsg('The hierarchy of JSON Schema is not correct! Please include as schema[idx].function.parameters.properties.');
        return;
      }

      try {
        let oneClone = one;

        if (one.name && (one.parameters)) { // Custom OpenAI syntax
          oneClone = JSON.parse(JSON.stringify(one.parameters));
          // eslint-disable-next-line no-unused-vars
          oneClone = { ...oneClone, ...(({ name, parameters, ...o }) => o)(one) };  // Remove name and parameters
        }
        else if (one.type === 'function') {
          oneClone = JSON.parse(JSON.stringify(one.function.parameters));
          // eslint-disable-next-line no-unused-vars
          oneClone = { ...oneClone, ...(({ name, parameters, ...o }) => o)(one.function) };  // Remove name and parameters
        }

        ajv.compile(oneClone);
        setResults(x => [...x, true]);
      }
      catch (e) {
        setResults(x => [...x, { name: one.name, message: e.message }]);
      }
    }
  }, [isStrict, jsonSchema, results, setResults]);

  const handleIsStrictChange = useCallback(() => {
    setIsStrict(s => !s);
  }, [setIsStrict])

  return (
    <Stack>
      <FloatingLabel as='header' className="ValidateSchema-header">
        Is JSON Schema Valid
        <Stack direction='horizontal' style={{ alignItems: 'center' }}>
          <Link style={link} to='/'>Main page</Link>
        </Stack>
      </FloatingLabel>

      <Stack direction='vertical' className="App" style={{ margin: '40px', alignItems: 'center' }} >
        <ToggleButton style={{ width: 200 }} type='radio' variant='secondary' checked={isStrict} onClick={handleIsStrictChange}>{isStrict ? 'Disable Strict' : 'Enable Strict'}</ToggleButton>
        <Stack direction='horizontal' gap={3} className="mx-auto"  >
          <Form.Control id="json" placeholder={'E.g.,\n' + JSON.stringify(jsonSchemaTemplate, undefined, 1)} style={textArea} onBlur={handleJsonSchemaChange} as="textarea" rows={20} />
        </Stack>

        <Stack direction='horizontal' className="mx-auto">
          <Button onClick={handleIsJSONSchemaValid} style={{ width: '100px', margin: '0 10px' }}>Is Valid</Button>
          <Button onClick={() => handleIsJSONSchemaValid('2019')} variant='secondary' style={{ width: '100px', margin: '0 10px' }}>ajv-2019</Button>
          <Button onClick={() => handleIsJSONSchemaValid('2020')} variant='secondary' style={{ width: '100px', margin: '0 10px' }}>ajv-2020</Button>
        </Stack>

        <Stack direction='vertical'>
          {
            alertMsg &&
            <>
              <FormLabel>Result:</FormLabel>
              <Alert dismissible closeVariant='danger'> {alertMsg}</Alert>
            </>
          }
          {
            results.filter(x => x === true).length.toString() + ' validated 	 | ' + results.filter(x => x !== true).length.toString() + ' Failed.'
          }
          <Stack direction='horizontal' gap={2}>
            {
              results.map((x, i) => x === true ? <span key={`r-${i}`}>&#10004;</span> : <span key={`r-${i}`}>&#10060;</span>)
            }
          </Stack>
          {
            results.map((result, idx) => result !== true &&
              <Alert dismissible variant='danger' key={`msg-${idx}`} onClose={() => setResults([])}>
                <Alert.Heading>Error</Alert.Heading>
                {`${idx + 1}. ${JSON.stringify(result)}`}
              </Alert>
            )
          }
        </Stack>
      </Stack>
    </Stack >
  )
}

export default IsJSONSchemaValid;